/**
 * ELARA Health Check
 * -----------------------------------------------------------------
 * Premium admin-only platform integrity audit card + report modal.
 * Designed to be visually striking and executive-friendly.
 *
 * Plug-in friendly: every category in the backend response is rendered
 * generically, so adding a new check on the server appears here
 * automatically without UI changes.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
    Activity,
    AlertTriangle,
    CheckCircle2,
    ExternalLink,
    Info,
    Loader2,
    Music2,
    PlayCircle,
    ReceiptText,
    RefreshCw,
    ShieldAlert,
    ShieldCheck,
    Sparkles,
    WalletCards,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

// -----------------------------------------------------------------------------
// Visual helpers
// -----------------------------------------------------------------------------
const statusMeta = {
    healthy:   { label: 'Healthy',            color: '#00FF9D', ring: 'stroke-emerald-400', text: 'text-emerald-400', glow: 'shadow-[0_0_60px_-12px_rgba(0,255,157,0.45)]', icon: ShieldCheck },
    attention: { label: 'Attention Required', color: '#FFB700', ring: 'stroke-amber-400',  text: 'text-amber-400',   glow: 'shadow-[0_0_60px_-12px_rgba(255,183,0,0.45)]', icon: ShieldAlert },
    critical:  { label: 'Critical',           color: '#FF4757', ring: 'stroke-rose-500',   text: 'text-rose-500',    glow: 'shadow-[0_0_60px_-12px_rgba(255,71,87,0.45)]', icon: AlertTriangle },
    unknown:   { label: 'Not Yet Run',        color: '#7C3AED', ring: 'stroke-violet-500', text: 'text-violet-400',  glow: '',                                              icon: Activity },
};

const severityMeta = {
    info:     { label: 'Info',     color: 'border-sky-500/40 bg-sky-500/10 text-sky-400' },
    warning:  { label: 'Warning',  color: 'border-amber-500/40 bg-amber-500/10 text-amber-400' },
    critical: { label: 'Critical', color: 'border-rose-500/40 bg-rose-500/10 text-rose-400' },
};

const categoryMeta = {
    revenue: {
        label: 'Revenue Integrity',
        icon: WalletCards,
        verified: 'Revenue integrity verified',
    },
    metadata: {
        label: 'Catalogue Integrity',
        icon: Music2,
        verified: 'Catalogue integrity verified',
    },
    finance: {
        label: 'Financial Integrity',
        icon: ReceiptText,
        verified: 'Financial integrity verified',
    },
};

const severityOrder = {
    critical: 0,
    warning: 1,
    info: 2,
};

const getSeverityCounts = (issues = []) => issues.reduce(
    (counts, issue) => {
        const severity = issue.severity || 'warning';
        counts[severity] = (counts[severity] || 0) + 1;
        return counts;
    },
    { critical: 0, warning: 0, info: 0 }
);

const getCategoryPenalty = (issues = [], category) => issues
    .filter((issue) => issue.category === category)
    .reduce((total, issue) => total + Number(issue.penalty || 0), 0);

const formatRunAt = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
};

// -----------------------------------------------------------------------------
// Score Ring (SVG circular progress)
// -----------------------------------------------------------------------------
const ScoreRing = ({ score = 0, size = 168, stroke = 12, color = '#7C3AED' }) => {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const offset = c - (Math.max(0, Math.min(100, score)) / 100) * c;
    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="-rotate-90">
                <circle
                    cx={size / 2} cy={size / 2} r={r}
                    fill="none" strokeWidth={stroke}
                    className="stroke-border/30"
                />
                <circle
                    cx={size / 2} cy={size / 2} r={r}
                    fill="none" strokeWidth={stroke}
                    strokeDasharray={c}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    style={{ stroke: color, transition: 'stroke-dashoffset 900ms cubic-bezier(.22,1,.36,1)' }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-5xl font-heading font-bold leading-none tracking-tight">{score}</div>
                <div className="text-xs text-muted-foreground mt-1 tracking-wider">OUT OF 100</div>
            </div>
        </div>
    );
};

// -----------------------------------------------------------------------------
// Category mini-score (used inside the report modal)
// -----------------------------------------------------------------------------
const CategoryScorePill = ({
    label,
    score,
    accent,
    icon: Icon,
    penalty = 0,
}) => {
    const tone = score >= 85
        ? 'text-emerald-400'
        : score >= 60
            ? 'text-amber-400'
            : 'text-rose-400';

    return (
        <div className="flex-1 rounded-xl border border-border/40 bg-card/40 p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {label}
                </div>
                <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{
                        background: `${accent}18`,
                        color: accent,
                    }}
                >
                    <Icon className="w-4 h-4" />
                </div>
            </div>

            <div className="flex items-end justify-between gap-2">
                <div className={`text-3xl font-heading font-bold ${tone}`}>
                    {score}
                    <span className="text-base text-muted-foreground font-normal">
                        /100
                    </span>
                </div>

                {penalty > 0 && (
                    <div className="text-xs font-medium text-rose-400">
                        −{penalty}
                    </div>
                )}
            </div>
        </div>
    );
};

// -----------------------------------------------------------------------------
// Health Check Card (lives on Admin Dashboard)
// -----------------------------------------------------------------------------
const HealthCheckCard = () => {
    const [latest, setLatest] = useState(null);
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    const fetchLatest = async () => {
        setLoading(true);
        try {
            const res = await api.get('/health-check/latest');
            setLatest(res.data && res.data.run_at ? res.data : null);
        } catch {
            setLatest(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLatest(); }, []);

    const runCheck = async () => {
        setRunning(true);
        try {
            const res = await api.post('/health-check/run');
            setLatest(res.data);
            toast.success('Health check complete', {
                description: `Score: ${res.data.overall_score}/100 — ${res.data.total_issues} issue(s) found`,
            });
            setReportOpen(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to run health check');
        } finally {
            setRunning(false);
        }
    };

    const meta = statusMeta[latest?.status] || statusMeta.unknown;
    const StatusIcon = meta.icon;
    const severityCounts = getSeverityCounts(latest?.issues || []);

    return (
        <>
            <Card
                className={`relative overflow-hidden border-border/40 ${meta.glow} transition-shadow duration-700`}
                data-testid="health-check-card"
                style={{
                    background: 'radial-gradient(120% 100% at 0% 0%, rgba(124,58,237,0.18) 0%, rgba(124,58,237,0) 45%), radial-gradient(120% 100% at 100% 100%, rgba(0,255,157,0.10) 0%, rgba(0,255,157,0) 50%), hsl(var(--card) / 0.6)',
                }}
            >
                {/* Decorative grid */}
                <div className="pointer-events-none absolute inset-0 opacity-[0.07]"
                     style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.4) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
                <CardContent className="relative p-6 md:p-8">
                    <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center">
                                    <Sparkles className="w-5 h-5 text-primary" />
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-secondary animate-pulse" />
                            </div>
                            <div>
                                <div className="text-[11px] tracking-[0.18em] text-primary uppercase font-medium">ELARA</div>
                                <div className="text-xl font-heading font-bold leading-tight">Platform Health Centre</div>
                            </div>
                        </div>
                        <Badge variant="outline" className={`gap-1.5 ${meta.text} border-current/30`} data-testid="health-status-badge">
                            <StatusIcon className="w-3.5 h-3.5" />
                            {meta.label}
                        </Badge>
                    </div>

                    <div className="flex flex-col md:flex-row items-center md:items-stretch gap-6 md:gap-10">
                        {/* Score ring */}
                        <div className="flex-shrink-0 flex items-center justify-center">
                            <ScoreRing score={latest?.overall_score || 0} color={meta.color} />
                        </div>

                        {/* Metrics */}
                        <div className="flex-1 self-center w-full">
                            <div className="grid grid-cols-3 gap-4">
                                <Metric
                                    label="Revenue"
                                    value={latest?.revenue_score ?? '—'}
                                />
                                <Metric
                                    label="Catalogue"
                                    value={latest?.metadata_score ?? '—'}
                                />
                                <Metric
                                    label="Finance"
                                    value={latest?.finance_score ?? '—'}
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-border/40">
                                <SeverityMetric
                                    label="Critical"
                                    value={latest ? severityCounts.critical : '—'}
                                    tone="text-rose-400"
                                />
                                <SeverityMetric
                                    label="Warnings"
                                    value={latest ? severityCounts.warning : '—'}
                                    tone="text-amber-400"
                                />
                                <SeverityMetric
                                    label="Information"
                                    value={latest ? severityCounts.info : '—'}
                                    tone="text-sky-400"
                                />
                            </div>

                            <div className="text-xs text-muted-foreground border-t border-border/40 pt-3 mt-4">
                                Last verified:{' '}
                                <span
                                    className="text-foreground/80"
                                    data-testid="health-last-run"
                                >
                                    {formatRunAt(latest?.run_at)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                        <Button
                            onClick={runCheck}
                            disabled={running || loading}
                            data-testid="run-health-check-btn"
                            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 h-11"
                        >
                            {running ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running audit…</>
                            ) : (
                                <><PlayCircle className="w-4 h-4 mr-2" /> Run Platform Audit</>
                            )}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setReportOpen(true)}
                            disabled={!latest}
                            data-testid="view-health-report-btn"
                            className="flex-1 h-11"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            View Health Report
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <HealthCheckReport
                open={reportOpen}
                onOpenChange={setReportOpen}
                run={latest}
                onRerun={runCheck}
                running={running}
            />
        </>
    );
};

const Metric = ({ label, value }) => (
    <div className="space-y-0.5">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {label}
        </div>
        <div className="text-2xl font-heading font-bold text-foreground">
            {value}
            {typeof value === 'number' && (
                <span className="text-xs text-muted-foreground font-normal ml-1">
                    /100
                </span>
            )}
        </div>
    </div>
);

const SeverityMetric = ({ label, value, tone }) => (
    <div className="rounded-lg bg-background/30 border border-border/30 px-3 py-2.5">
        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">
            {label}
        </div>
        <div className={`text-xl font-heading font-bold mt-0.5 ${tone}`}>
            {value}
        </div>
    </div>
);

// -----------------------------------------------------------------------------
// Health Check Report Modal
// -----------------------------------------------------------------------------
const HealthCheckReport = ({ open, onOpenChange, run, onRerun, running }) => {
    const navigate = useNavigate();
    if (!run) return null;
    const meta = statusMeta[run.status] || statusMeta.unknown;
    const severityCounts = getSeverityCounts(run.issues || []);

    const issuesByCategory = (run.issues || []).reduce((acc, issue) => {
        (acc[issue.category] = acc[issue.category] || []).push(issue);
        return acc;
    }, {});

    Object.values(issuesByCategory).forEach((items) => {
        items.sort(
            (a, b) => (
                (severityOrder[a.severity] ?? 1)
                - (severityOrder[b.severity] ?? 1)
            )
        );
    });

    const categoriesOrder = ['revenue', 'metadata', 'finance'];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl max-h-[90vh] overflow-y-auto"
                data-testid="health-report-modal"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-2xl font-heading">
                        <Sparkles className="w-5 h-5 text-primary" />
                        ELARA Platform Health Report
                    </DialogTitle>
                    <DialogDescription>
                        Generated {formatRunAt(run.run_at)} by {run.ran_by || 'admin'}
                    </DialogDescription>
                </DialogHeader>

                {/* Top: Overall + category scores */}
                <div className="flex flex-col md:flex-row gap-6 items-center md:items-stretch bg-background/50 rounded-2xl p-6 border border-border/40">
                    <div className="flex-shrink-0">
                        <ScoreRing score={run.overall_score} color={meta.color} size={140} stroke={10} />
                    </div>
                    <div className="flex-1 space-y-3 w-full">
                        <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${meta.text}`}>
                                {meta.label}
                            </span>
                            <span className="text-xs text-muted-foreground">•</span>
                            <span className="text-xs text-muted-foreground">
                                {severityCounts.critical} critical ·{' '}
                                {severityCounts.warning} warning ·{' '}
                                {severityCounts.info} information
                            </span>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <CategoryScorePill
                                label="Revenue"
                                score={run.revenue_score}
                                accent="#00FF9D"
                                icon={WalletCards}
                                penalty={getCategoryPenalty(run.issues, 'revenue')}
                            />
                            <CategoryScorePill
                                label="Catalogue"
                                score={run.metadata_score}
                                accent="#CCFF00"
                                icon={Music2}
                                penalty={getCategoryPenalty(run.issues, 'metadata')}
                            />
                            <CategoryScorePill
                                label="Finance"
                                score={run.finance_score}
                                accent="#00F2EA"
                                icon={ReceiptText}
                                penalty={getCategoryPenalty(run.issues, 'finance')}
                            />
                        </div>
                    </div>
                </div>

                {/* Per-category issues */}
                <div className="space-y-6 mt-2">
                    {categoriesOrder.map((cat) => {
                        const cmeta = categoryMeta[cat] || {
                            label: cat,
                            icon: Activity,
                            verified: 'Integrity verified',
                        };
                        const CategoryIcon = cmeta.icon;
                        const items = issuesByCategory[cat] || [];

                        return (
                            <section key={cat} data-testid={`health-section-${cat}`}>
                                <div className="flex items-center justify-between gap-3 mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <CategoryIcon className="w-4 h-4 text-primary" />
                                        </div>
                                        <h3 className="text-base font-heading font-bold">
                                            {cmeta.label}
                                        </h3>
                                    </div>

                                    <div className="text-xs text-muted-foreground">
                                        {items.length} finding{items.length === 1 ? '' : 's'}
                                    </div>
                                </div>

                                {items.length === 0 ? (
                                    <div className="flex items-start gap-3 text-sm text-emerald-400 bg-emerald-500/5 border border-emerald-500/30 rounded-xl px-4 py-3.5">
                                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <div className="font-medium">
                                                {cmeta.verified}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                No inconsistencies were detected in this area.
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {items.map((issue) => (
                                            <IssueRow key={issue.key} issue={issue} onNavigate={(route) => { onOpenChange(false); navigate(route); }} />
                                        ))}
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
                    <Button onClick={onRerun} disabled={running} data-testid="rerun-health-check-btn">
                        {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</> : <><RefreshCw className="w-4 h-4 mr-2" /> Re-run</>}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const IssueRow = ({ issue, onNavigate }) => {
    const sev = severityMeta[issue.severity] || severityMeta.warning;
    return (
        <div className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border ${sev.color} px-4 py-3`} data-testid={`issue-${issue.key}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                        variant="outline"
                        className={`text-[10px] tracking-wider uppercase ${sev.color}`}
                    >
                        {issue.severity === 'info' && (
                            <Info className="w-3 h-3 mr-1" />
                        )}
                        {sev.label}
                    </Badge>
                    <span className="text-sm font-medium">
                        {issue.message}
                    </span>
                </div>
                {(issue.affected_percent !== undefined || issue.population) && (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                        {issue.population !== undefined && (
                            <Badge variant="outline" className="text-[10px] border-border/50">
                                {Number(issue.count || 0).toLocaleString('en-IN')}
                                {' / '}
                                {Number(issue.population || 0).toLocaleString('en-IN')}
                                {' affected'}
                            </Badge>
                        )}

                        {issue.affected_percent !== undefined && (
                            <Badge variant="outline" className="text-[10px] border-border/50">
                                {Number(issue.affected_percent).toLocaleString('en-IN', {
                                    maximumFractionDigits: 4,
                                })}
                                % of records
                            </Badge>
                        )}

                        {Number(issue.penalty || 0) > 0 && (
                            <Badge
                                variant="outline"
                                className="text-[10px] border-rose-500/30 text-rose-400"
                            >
                                −{issue.penalty} health points
                            </Badge>
                        )}
                    </div>
                )}

                {issue.suggested_action && (
                    <div className="text-xs text-muted-foreground mt-2">
                        <span className="text-foreground/70">
                            Recommended action:{' '}
                        </span>
                        {issue.suggested_action}
                    </div>
                )}
            </div>
            {issue.target_route && (
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onNavigate(issue.target_route)}
                    className="text-xs flex-shrink-0"
                    data-testid={`open-route-${issue.key}`}
                >
                    Open Module <ExternalLink className="w-3 h-3 ml-1.5" />
                </Button>
            )}
        </div>
    );
};

export default HealthCheckCard;
